import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { toggleMapOverlay } from "../inputCheck";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    .title {
        display: none;
    }
    .header {
        height: 100%;
    }
    .header img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    .header img:nth-child(2) {
        position: absolute;
        left: 0;
        bottom: 0;
        width: 600px;
        height: 600px;
    }
    .content {
        position: absolute;
        bottom: 56px;
        width: 100%;
        background: black;
        height: 128px;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .footer {
        position: absolute;
        bottom: 32px;
        width: 100%;
        display: flex;
        gap: 16px;
        justify-content: center;
        align-items: center;
    }
    .footer .buttons {
        display: flex;
        gap: 16px;
    }
    .footer .buttons div {
        display: flex;
        gap: 16px;
    }
    .footer .button {
        cursor: pointer;
        background: red;
        padding: 12px;
        display: inline-block;
    }
`;

const mainMenuStyles: string = css`
    .content {
        display: none;
    }

    .footer {
        z-index: 2;
        display: flex;
        width: 100%;
        height: 100%;
        max-width: 825px;
    }

    .middle-container {
        display: flex;
        position: absolute;
        top: 0;
        align-items: center;
        width: 100%;
        height: 100vh;
        flex-direction: column;
        max-width: 800px;

        .menu-bar,
        .logo {
            position: absolute;
        }

        .logo {
            top: 20px;
        }
    }

    .footer .button {
        transition: 0.1s;
        background: none;

        &:hover {
            transform: scale(1.1);
        }
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();
    // property to check what kind of button has been clicked.
    private _currentInitialAction: string = "none";

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
        if (this.shadowRoot.querySelector(".title")?.textContent === "Main Menu") {
            const mainMenuStylesElement: HTMLElement[] = htmlArray`
            <style>
                ${mainMenuStyles}
            </style>
            
            <div class="middle-container">
                <div class="menu-bar">
                    <img class="menu-bar-img" src="/assets/img/homescreen/menu-bar.svg" alt="homescreenBar">
                </div>

                <div class="logo">
                    <img class="logo-img" src="/assets/img/homescreen/logo.png" alt="homescreenLogo">
                </div>
            </div>
            
            `;
            this.shadowRoot.append(...mainMenuStylesElement);
            console.log("The component has rendered successfully.");
        }
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */

    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content">
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
        `;
    }

    private makeButtons(): Array<HTMLElement> {
        const buttons: Array<HTMLElement> = [];
        if (this._currentGameState?.objects) {
            // eslint-disable-next-line
            for (const button of <GameObjectReference[]>this._currentGameState?.objects) {
                if (this._currentInitialAction.toLowerCase() === "talk to" && button.isCharacter) {
                    buttons.push(this.renderGameObjectButton(button));
                }
                else if (this._currentInitialAction.toLowerCase() === "interact" && !button.isCharacter) {
                    buttons.push(this.renderGameObjectButton(button));
                }
            }
        }
        return buttons;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div>${this._selectedActionButton ? this.makeButtons().map(button => button) : ""}</div>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}"> ${button.name} </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        // CONTINUE THE FILTER HERE!
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;
        element.addEventListener("click", () => this.handleClickGameObject(button));
        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        console.log(button);
        if (button.alias === "map") {
            console.log("map");
            toggleMapOverlay();
        }
        // this the initial action to determine later if "talk to" or "interact" have been pressed for filtering the items/chars later.
        this._currentInitialAction = button.name.toLowerCase();
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        console.log(button);

        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
